import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Activity, ArrowDownToLine, ArrowDownWideNarrow, ArrowRight, ChartNoAxesGantt, CircleDollarSign, MapPin, Truck, X } from 'lucide-react';
import { Drill } from './indicators';
import { ColumnHint, DataHint } from './data-hint';
import JourneyFlow from './journey-flow';
import {
  DEMO_NOW,
  DemoState,
  EventKey,
  Trip,
  STATUS_LABELS,
  businessDay,
  clock,
  dateTime,
  duration,
  exportCsv,
  metrics,
  money,
  shift,
  hasTripData,
} from './model';

export const EVENT_LABELS: Record<EventKey, string> = {
  scheduled: 'Agendamento',
  arrival: 'Chegada · aguardando descarga',
  unloaded: 'Material entregue · fim do descarregamento',
  docked: 'Encostou para carregamento',
  loaded: 'Carregado · aguardando saída',
  released: 'Liberado operação',
  exit: 'Saída',
};
const EVENT_DESCRIPTIONS: Record<EventKey, string> = {
  scheduled:
    'Horário de referência do agendamento. Se o veículo chegar antes, a contagem do recebimento começa neste horário.',
  arrival:
    'Horário da chegada confirmada. Chegada após o agendamento torna a viagem sem direito à cobrança de estadia.',
  unloaded:
    'Conclusão da descarga e encerramento do tempo de recebimento (REC).',
  docked:
    'Veículo encostou para carregar. Marca o início do tempo de expedição (EXP).',
  loaded:
    'Carregamento concluído. Encerra a apuração de EXP, mesmo que a saída física aconteça depois.',
  released:
    'Liberação operacional. Junto ao material entregue, encerra a operação somente de descarga.',
  exit: 'Saída física do veículo. Registrada separadamente do encerramento da apuração de estadia.',
};
const EVENT_KEYS: EventKey[] = [
  'scheduled',
  'arrival',
  'unloaded',
  'docked',
  'loaded',
  'released',
  'exit',
];
export function Modal({
  title,
  description,
  children,
  onClose,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="stay-modal-overlay" />
        <Dialog.Content className="stay-modal">
          <div className="stay-modal-heading">
            <div>
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.Description>{description}</Dialog.Description>
            </div>
            <Dialog.Close aria-label="Fechar janela">
              <X size={20} />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
export function exportTrips(trips: Trip[], title = 'viagens-estadia') {
  exportCsv(
    `${title}.csv`,
    [
      [
        'Viagem',
        'Placa',
        'Local',
        'Data agendamento',
        'Transportadora',
        'Categoria',
        'Peso TON',
        'Situação',
        'REC',
        'EXP',
        'Total REC + EXP',
        'Horas excedentes',
        'Saldo franquia',
        'Valor R$',
        'Turno',
        'CNPJ NF',
        'Logradouro NF',
        'Validação',
      ],
      ...trips.map((trip) => {
        const value = metrics(trip);
        return [
          trip.sourceId || trip.id,
          trip.plate,
          trip.unit,
          dateTime(trip.events.scheduled),
          trip.carrier,
          trip.category,
          trip.sourceMissing?.includes('Tonelagem') ? '' : trip.tons,
          STATUS_LABELS[value.status],
          duration(value.rec),
          duration(value.exp),
          duration(value.total),
          duration(value.excess),
          value.eligible ? duration(value.remaining) : 'Sem direito',
          money(value.cents),
          shift(trip),
          trip.cnpj,
          trip.address,
          trip.validation,
        ];
      }),
    ],
    trips
  );
}
export function TripTable({
  trips,
  onSelect,
  empty = 'Nenhuma viagem corresponde aos filtros.',
  showRemaining = false,
}: {
  trips: Trip[];
  onSelect: (trip: Trip) => void;
  empty?: string;
  showRemaining?: boolean;
}) {
  const [page, setPage] = useState(0);
  const maxPage = Math.max(0, Math.ceil(trips.length / 15) - 1);
  const currentPage = Math.min(page, maxPage);
  return (
    <>
      <div className="stay-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Veículo / viagem</th>
              <th>
                {trips.some((trip) => hasTripData(trip, 'Unidade'))
                  ? 'Local / agendamento'
                  : 'Agendamento'}
              </th>
              <th>Transportadora</th>
              <th>
                <ColumnHint
                  label="REC"
                  description="Tempo de recebimento e descarga, contado até a ocorrência de material entregue."
                />
              </th>
              <th>
                <ColumnHint
                  label="EXP"
                  description="Tempo de expedição, do início do carregamento até a conclusão da carga."
                />
              </th>
              <th>
                <ColumnHint
                  label="Total"
                  description="Soma REC + EXP, sem incluir a espera entre recebimento e carregamento."
                />
              </th>
              {showRemaining && <th>Tempo restante</th>}
              <th>Excedente</th>
              <th>Valor</th>
              <th>Situação</th>
              <th>
                <span className="stay-sr">Detalhes</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {trips
              .slice(currentPage * 15, currentPage * 15 + 15)
              .map((trip) => {
                const value = metrics(trip);
                return (
                  <tr key={trip.id}>
                    <td>
                      <button
                        className="stay-link"
                        onClick={() => onSelect(trip)}
                      >
                        <b>{trip.plate}</b>
                        <small>{trip.sourceId || trip.id}</small>
                      </button>
                    </td>
                    <td>
                      {hasTripData(trip, 'Unidade') && trip.unit}
                      <small>{dateTime(trip.events.scheduled)}</small>
                    </td>
                    <td>{trip.carrier}</td>
                    <td>{duration(value.rec)}</td>
                    <td>{duration(value.exp)}</td>
                    <td>
                      <b>{duration(value.total)}</b>
                    </td>
                    {showRemaining && (
                      <td>
                        <b>
                          {hasTripData(trip, 'Horas restantes')
                            ? duration(value.remaining)
                            : 'Não informado'}
                        </b>
                      </td>
                    )}
                    <td>{duration(value.excess)}</td>
                    <td>{money(value.cents)}</td>
                    <td>
                      <span className={`stay-pill ${value.status}`}>
                        {STATUS_LABELS[value.status]}
                      </span>
                    </td>
                    <td>
                      <button
                        aria-label={`Detalhes ${trip.id}`}
                        className="stay-icon-button"
                        onClick={() => onSelect(trip)}
                      >
                        <ArrowRight size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {!trips.length && <div className="stay-empty-state">{empty}</div>}
      <div className="stay-pagination">
        <span>
          {trips.length} viagens · página {currentPage + 1} de {maxPage + 1}
        </span>
        <button
          disabled={currentPage === 0}
          onClick={() => setPage(currentPage - 1)}
        >
          Anterior
        </button>
        <button
          disabled={currentPage === maxPage}
          onClick={() => setPage(currentPage + 1)}
        >
          Próxima
        </button>
      </div>
    </>
  );
}
export function DrillDialog({
  drill,
  onSelect,
  onClose,
}: {
  drill: Drill;
  onSelect: (trip: Trip) => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title={drill.title}
      description={`${drill.trips.length} viagens compõem este indicador.`}
      onClose={onClose}
    >
      <div className="stay-toolbar">
        <button onClick={() => exportTrips(drill.trips)}>
          <ArrowDownToLine size={15} /> Exportar detalhamento
        </button>
      </div>
      {drill.showRemaining && (
        <p>
          O indicador considera o saldo da franquia. REC e EXP mostram os tempos
          já apurados.
        </p>
      )}
      <TripTable
        trips={drill.trips}
        onSelect={onSelect}
        showRemaining={drill.showRemaining}
      />
    </Modal>
  );
}
export function TripDetails({
  trip,
  hideSourceStatus = false,
  onClose,
}: {
  trip: Trip;
  hideSourceStatus?: boolean;
  state: DemoState;
  onClose: () => void;
}) {
  const value = metrics(trip);
  return (
    <Modal
      title={`${trip.plate} · ${trip.sourceId || trip.id}`}
      description={[
        trip.carrier,
        hasTripData(trip, 'Unidade') ? trip.unit : '',
        trip.operation,
      ]
        .filter(Boolean)
        .join(' · ')}
      onClose={onClose}
    >
      <div className="stay-detail-summary">
        <span className={`stay-pill ${value.status}`}>
          {STATUS_LABELS[value.status]}
        </span>
        {!hideSourceStatus && <span>{value.phase}</span>}
        <button onClick={() => exportTrips([trip], trip.id)}>
          <ArrowDownToLine size={14} /> Exportar viagem
        </button>
      </div>
      <div className="stay-detail-grid">
        <dl>
          {[
            ['Recebimento · REC', duration(value.rec)],
            ['Expedição · EXP', duration(value.exp)],
            ['Total · REC + EXP', duration(value.total)],
            [
              'Saldo da franquia',
              value.eligible ? duration(value.remaining) : 'Sem direito',
            ],
            ['Horas adicionais', duration(value.excess)],
            ['Tarifa por hora', money(4662)],
            ['Valor da estadia', money(value.cents)],
            [
              'Categoria / peso',
              trip.sourceMissing?.includes('Tonelagem')
                ? 'Não informado'
                : `${trip.category} · ${trip.tons.toLocaleString('pt-BR')} t`,
            ],
            ['Turno', shift(trip)],
          ]
            .filter(
              ([label]) =>
                label !== 'Categoria / peso' ||
                (hasTripData(trip, 'Tonelagem') &&
                  hasTripData(trip, 'Categoria de produto'))
            )
            .map(([label, text]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{text}</dd>
              </div>
            ))}
        </dl>
        <div>
          <h3>Ocorrências da viagem</h3>
          <ol className="stay-event-list">
            {(Object.keys(EVENT_LABELS) as EventKey[])
              .filter(
                (key) =>
                  Boolean(trip.events[key]) &&
                  (trip.operation === 'Descarga'
                    ? !['docked', 'loaded'].includes(key)
                    : key !== 'released')
              )
              .map((key) => (
                <li key={key} className={trip.events[key] ? 'received' : ''}>
                  <i />
                  <div>
                    <strong>{EVENT_LABELS[key]}</strong>
                    <span>{dateTime(trip.events[key])}</span>
                  </div>
                </li>
              ))}
          </ol>
        </div>
      </div>
      {!trip.assessment && (
        <div className="stay-callout">
          {value.eligible
            ? 'Regra 01 · chegada antecipada ou pontual. A contagem começa no agendamento; tempo entre recebimento e expedição é mostrado como espera e não compõe REC + EXP.'
            : 'Regra 02 · chegada após o agendamento. A permanência é exibida, mas esta viagem não gera cobrança nem pode ser faturada.'}
          <br />
          {!trip.assessment &&
            (trip.operation === 'Descarga'
              ? 'Encerramento: Material entregue + Liberado operação.'
              : 'Encerramento: Carregado aguardando saída. A saída física é exibida separadamente.')}
        </div>
      )}
      {trip.validationReason && (
        <div className="stay-callout">
          Parecer operacional: {trip.validationReason}
        </div>
      )}
      {hasTripData(trip, 'CNPJ e endereço da NF') && (
        <div className="stay-fiscal">
          <strong>Identificação fiscal da NF</strong>
          <span>
            {trip.site} · CNPJ {trip.cnpj} · {trip.address}
          </span>
        </div>
      )}
      {Boolean(trip.history?.length) && (
        <>
          <h3>Histórico operacional</h3>
          <ol className="stay-audit">
            {trip.history!.map((event, index) => (
              <li key={`${event.at}-${index}`}>
                <span>
                  {dateTime(event.at)} · {event.actor}
                </span>
                <p>{event.message}</p>
              </li>
            ))}
          </ol>
        </>
      )}
    </Modal>
  );
}
export function Timeline({
  trips,
  onSelect,
  day,
  positionAt = DEMO_NOW,
}: {
  trips: Trip[];
  onSelect: (trip: Trip) => void;
  day: string;
  positionAt?: string;
}) {
  const [onlyActive, setOnlyActive] = useState(true);
  const [onlyFree, setOnlyFree] = useState(false);
  const [ordering, setOrdering] = useState('critical');
  const [display, setDisplay] = useState('flow');
  const rows = trips
    .filter(
      (trip) =>
        (trip.circuit || trip.sourceMissing?.includes('Circuito')) &&
        (!onlyActive || !metrics(trip).complete) &&
        (!onlyFree || metrics(trip).cents === 0)
    )
    .sort((first, second) =>
      ordering === 'plate'
        ? first.plate.localeCompare(second.plate)
        : metrics(second).total - metrics(first).total
    );
  const dayStart = Date.parse(`${day}T00:00:00-03:00`);
  const dayEnd = dayStart + 86400000;
  const position = (value: string) =>
    Math.max(0, Math.min(100, (Date.parse(value) - dayStart) / 864000));
  function apportionedStage(
    trip: Trip,
    label: string,
    minutes: number,
    left: number,
    width: number,
    tone: string
  ) {
    if (minutes <= 0 || width <= 0) return null;
    return (
      <DataHint
        title={`${label} · ${duration(minutes)}`}
        description={`Tempo apurado ${trip.assessment ? 'recebido da origem' : 'para esta viagem'}. A largura representa a participação em REC + EXP, não um horário de início ou fim.`}
      >
        <span
          tabIndex={0}
          role="img"
          aria-label={`${label}: ${duration(minutes)}`}
          className={`stay-time-segment ${tone}`}
          style={{ left: `${left}%`, width: `${width}%` }}
        >
          {width > 22 ? `${label} · ${duration(minutes)}` : duration(minutes)}
        </span>
      </DataHint>
    );
  }
  function segment(start: string, end: string, tone: string, label: string) {
    if (Date.parse(start) >= dayEnd || Date.parse(end) <= dayStart) return null;
    const left = position(start);
    const width = position(end) - left;
    return (
      <DataHint
        title={`${label} · ${dateTime(start)} → ${dateTime(end)}`}
        description={`${tone === 'receiving' ? 'Tempo de recebimento e descarga. Compõe REC e consome a franquia de 10 horas.' : tone === 'shipping' ? 'Tempo de expedição e carregamento. Compõe EXP e consome a franquia de 10 horas.' : 'Intervalo entre as etapas, com o veículo aguardando. Não compõe a soma REC + EXP.'} Duração desta etapa: ${duration(Math.max(0, Math.round((Date.parse(end) - Date.parse(start)) / 60000)))}. Os horários completos incluem a passagem de dia, quando houver.`}
      >
        <span
          tabIndex={0}
          role="img"
          aria-label={`${label}: ${dateTime(start)} até ${dateTime(end)}`}
          className={`stay-time-segment ${tone}`}
          style={{ left: `${left}%`, width: `${width}%` }}
        >
          {width > 15 ? label : ''}
        </span>
      </DataHint>
    );
  }
  return (
    <section className="stay-card stay-timeline-card">
      <div className="stay-card-heading stay-yard-heading">
        <div>
          <h2>Pátio & docas</h2>
          <p>
            {trips.some((trip) => trip.sourceMissing?.includes('Circuito'))
              ? 'Viagens por agendamento'
              : 'Franco da Rocha ↔ Paulínia · Circuito UNO'}
          </p>
        </div>
        <span className="stay-yard-count">
          <Truck size={14} aria-hidden="true" />
          {rows.length} {rows.length === 1 ? 'viagem' : 'viagens'}
        </span>
      </div>
      <div className="stay-toolbar stay-yard-toolbar" role="group" aria-label="Opções do pátio e docas">
        <label className="stay-yard-select">
          <ChartNoAxesGantt size={16} aria-hidden="true" />
          <select
            aria-label="Visualização das etapas"
            value={display}
            onChange={(event) => setDisplay(event.target.value)}
          >
            <option value="flow">Fluxo da viagem</option>
            <option value="duration">Tempos por etapa</option>
            <option value="clock">Horários do dia</option>
          </select>
        </label>
        <label className={`stay-yard-filter${onlyActive ? ' is-active' : ''}`}>
          <input
            type="checkbox"
            aria-label="Somente em operação"
            checked={onlyActive}
            onChange={(event) => setOnlyActive(event.target.checked)}
          />
          <Activity size={15} aria-hidden="true" />
          Em operação
        </label>
        <label className={`stay-yard-filter${onlyFree ? ' is-active' : ''}`}>
          <input
            type="checkbox"
            aria-label="Somente sem valor a pagar"
            checked={onlyFree}
            onChange={(event) => setOnlyFree(event.target.checked)}
          />
          <CircleDollarSign size={15} aria-hidden="true" />
          Sem valor a pagar
        </label>
        <label className="stay-yard-select stay-yard-order">
          <ArrowDownWideNarrow size={16} aria-hidden="true" />
          <select
            aria-label="Ordenar viagens"
            value={ordering}
            onChange={(event) => setOrdering(event.target.value)}
          >
            <option value="critical">Maior permanência</option>
            <option value="plate">Placa A–Z</option>
          </select>
        </label>
      </div>
      <div className="stay-timeline-scroll">
        <div className="stay-timeline">
          <div className="stay-timeline-head">
            <span>VEÍCULO / VIAGEM</span>
            <div className="stay-time-axis">
              {display === 'flow' ? (
                <span>
                  Fluxo cronológico · horários de Brasília · datas por viagem
                </span>
              ) : display === 'duration' ? (
                <span>Distribuição do tempo por etapa · REC + EXP</span>
              ) : (
                Array.from({ length: 9 }, (_, index) => (
                  <DataHint
                    key={index}
                    title={`${String(index * 3).padStart(2, '0')}:00 · escala do dia`}
                    description={`${index === 8 ? 'Meia-noite do dia seguinte, encerrando a escala de 24 horas.' : `Horário de ${String(index * 3).padStart(2, '0')}:00 no dia selecionado.`} As barras são posicionadas por horário de Brasília; a escala mostra o horário do relógio, não a duração da permanência.`}
                  >
                    <span tabIndex={0}>
                      {String(index * 3).padStart(2, '0')}:00
                    </span>
                  </DataHint>
                ))
              )}
            </div>
            <span>SITUAÇÃO / ETAPA</span>
            <ColumnHint
              label="REC + EXP"
              description="Soma do tempo de recebimento/descarga (REC) e expedição/carregamento (EXP). O intervalo de espera entre as etapas não entra nessa soma."
            />
            <ColumnHint
              label="HORAS RESTANTES"
              description="Saldo da franquia de 10 horas menos a soma REC + EXP. Positivo: tempo disponível; zero: franquia atingida; negativo: tempo excedente. Sem direito é exibido em cinza."
            />
          </div>
          {rows.map((trip) => {
            const value = metrics(trip);
            const start = value.eligible
              ? trip.events.scheduled
              : trip.events.arrival;
            const phaseTotal = value.rec + value.exp;
            const recShare =
              phaseTotal > 0 ? (value.rec / phaseTotal) * 100 : 0;
            return (
              <button
                key={trip.id}
                className="stay-time-row"
                onClick={() => onSelect(trip)}
                aria-label={`Abrir viagem ${trip.id}`}
              >
                <div className="stay-time-vehicle">
                  <b>
                    <Truck size={18} />
                    {trip.plate}
                  </b>
                  <small>
                    {trip.sourceId || trip.id} · {trip.carrier}
                  </small>
                  {hasTripData(trip, 'Unidade') && (
                    <span>
                      <MapPin size={11} /> {trip.unit}
                    </span>
                  )}
                </div>
                {display === 'flow' ? (
                  <JourneyFlow trip={trip} />
                ) : (
                  <div
                    className={`stay-time-track ${display === 'duration' ? 'stay-time-duration' : ''}`}
                  >
                    {display === 'duration' && (
                      <>
                        {apportionedStage(
                          trip,
                          'Recebimento (REC)',
                          value.rec,
                          0,
                          recShare,
                          'receiving'
                        )}
                        {apportionedStage(
                          trip,
                          'Expedição (EXP)',
                          value.exp,
                          recShare,
                          phaseTotal > 0 ? 100 - recShare : 0,
                          'shipping'
                        )}
                        <div className="stay-stage-times">
                          <span className="receiving">
                            REC · {duration(value.rec)}
                          </span>
                          <span className="shipping">
                            EXP · {duration(value.exp)}
                          </span>
                        </div>
                      </>
                    )}
                    {display === 'clock' &&
                      (!trip.assessment ||
                        (value.rec > 0 && trip.events.unloaded)) &&
                      segment(
                        start,
                        trip.events.unloaded || trip.events.exit || positionAt,
                        'receiving',
                        'Recebimento'
                      )}
                    {display === 'clock' &&
                      trip.events.unloaded &&
                      (trip.events.docked || trip.events.released) &&
                      segment(
                        trip.events.unloaded,
                        trip.events.docked ||
                          trip.events.released ||
                          trip.events.loaded ||
                          trip.events.exit ||
                          positionAt,
                        'waiting',
                        'Espera'
                      )}
                    {display === 'clock' &&
                      trip.events.docked &&
                      segment(
                        trip.events.docked,
                        trip.events.loaded || positionAt,
                        'shipping',
                        'Expedição'
                      )}
                    {display === 'clock' &&
                      EVENT_KEYS.map((key) => {
                        const event = trip.events[key as EventKey];
                        if (
                          !event ||
                          Date.parse(event) < dayStart ||
                          Date.parse(event) >= dayEnd
                        )
                          return null;
                        const shared = EVENT_KEYS.filter(
                          (eventKey) =>
                            trip.events[eventKey] &&
                            Date.parse(trip.events[eventKey]!) ===
                              Date.parse(event)
                        );
                        if (shared[0] !== key) return null;
                        const eventLabel = shared
                          .map((eventKey) => EVENT_LABELS[eventKey])
                          .join(' / ');
                        return (
                          <DataHint
                            key={key}
                            title={`${eventLabel} · ${dateTime(event)}`}
                            description={shared
                              .map((eventKey) => EVENT_DESCRIPTIONS[eventKey])
                              .join(' ')}
                          >
                            <span
                              tabIndex={0}
                              role="img"
                              aria-label={`${eventLabel} · ${dateTime(event)}`}
                              className={`stay-time-marker ${key}`}
                              style={{ left: `${position(event)}%` }}
                            />
                          </DataHint>
                        );
                      })}
                    <DataHint
                      title="Agendamento e chegada"
                      description={`Agendamento: ${dateTime(trip.events.scheduled)}. Chegada: ${dateTime(trip.events.arrival)}. ${trip.assessment ? 'Tempos, saldo e valor exibidos conforme apuração recebida da origem.' : value.eligible ? 'Chegada antecipada ou pontual: a contagem começa no agendamento.' : 'Chegada atrasada: a permanência é acompanhada, mas não gera cobrança de estadia.'}`}
                    >
                      <div className="stay-time-caption" tabIndex={0}>
                        {businessDay(trip.events.scheduled) !== day
                          ? `Iniciada em ${dateTime(trip.events.scheduled)}`
                          : `Agend. ${clock(trip.events.scheduled)} · chegada ${clock(trip.events.arrival)}`}{' '}
                        ·{' '}
                        {value.complete
                          ? 'Encerramento recebido'
                          : 'Em operação'}
                      </div>
                    </DataHint>
                  </div>
                )}
                <div>
                  <DataHint
                    title={STATUS_LABELS[value.status]}
                    description={
                      value.status === 'none'
                        ? 'O veículo chegou após o agendamento. Esta viagem não pode gerar cobrança de estadia, mesmo que permaneça mais de 10 horas.'
                        : value.status === 'pay'
                          ? `A viagem ultrapassou a franquia de 10 horas em ${duration(value.excess)}. Valor apurado: ${money(value.cents)}, proporcional aos minutos excedentes.`
                          : value.status === 'risk'
                            ? `Restam ${duration(value.remaining)} até atingir a franquia de 10 horas. A operação deve priorizar esta viagem.`
                            : `A viagem permanece dentro da franquia de 10 horas, com ${duration(value.remaining)} disponíveis.`
                    }
                  >
                    <span tabIndex={0} className={`stay-pill ${value.status}`}>
                      {STATUS_LABELS[value.status]}
                    </span>
                  </DataHint>
                </div>
                <DataHint
                  title={`Permanência total · ${duration(value.total)}`}
                  description={`Recebimento (REC): ${duration(value.rec)}. Expedição (EXP): ${duration(value.exp)}. Total ${trip.assessment ? 'informado pela origem' : 'das etapas'}: ${duration(value.total)}.${trip.assessment && Math.abs(value.rec + value.exp - value.total) > 1 / 120 ? ' O total recebido difere da soma REC + EXP; os valores originais foram preservados.' : ''}`}
                >
                  <div className="stay-time-total" tabIndex={0}>
                    <b>{duration(value.total)}</b>
                    <small>
                      {duration(value.rec)} + {duration(value.exp)}
                    </small>
                  </div>
                </DataHint>
                <DataHint
                  title={
                    value.eligible
                      ? `Saldo da franquia · ${duration(value.remaining)}`
                      : 'Sem direito à estadia'
                  }
                  description={
                    !value.eligible
                      ? 'A chegada ocorreu após o agendamento. O saldo financeiro não se aplica e nenhuma estadia será cobrada.'
                      : value.remaining < 0
                        ? `O sinal negativo indica ${duration(value.excess)} além das 10 horas permitidas. A tarifa de R$ 46,62 por hora adicional resulta em ${money(value.cents)}.`
                        : `Ainda estão disponíveis ${duration(value.remaining)} da franquia de 10 horas. O saldo considera a permanência total de ${duration(value.total)}.`
                  }
                >
                  <div
                    className={`stay-time-balance ${value.status}`}
                    tabIndex={0}
                  >
                    <b>
                      {duration(value.eligible ? value.remaining : value.total)}
                    </b>
                    <small>
                      {value.cents
                        ? money(value.cents)
                        : value.eligible
                          ? 'Franquia de 10h'
                          : 'Chegada atrasada'}
                    </small>
                  </div>
                </DataHint>
              </button>
            );
          })}
        </div>
      </div>
      {!rows.length && (
        <div className="stay-empty-state">
          Nenhuma viagem nesta seleção. Ajuste os filtros ou inclua viagens
          encerradas.
        </div>
      )}
      <div className="stay-timeline-legend">
        <span>
          <i className="receiving" />
          Recebimento / descarga
        </span>
        <span>
          <i className="shipping" />
          Expedição / carregamento
        </span>
        {display === 'flow' && (
          <span>
            <i className="unclassified" /> Intervalo não detalhado
          </span>
        )}
        {display === 'clock' && (
          <span>
            <i className="waiting" />
            Aguardando / vazio
          </span>
        )}
        <span>
          {display === 'flow'
            ? 'Marcos em ordem cronológica, com horários visíveis. Cada linha abrange o período completo da viagem. A saída só é apresentada quando confirmada pelo status da origem.'
            : display === 'duration'
              ? 'REC e EXP conforme apuração. A situação atual aparece ao lado; abra a viagem para consultar os eventos e seus horários.'
              : 'Marcadores dos eventos recebidos: agendamento, chegada, fim de descarga, doca, fim de carga e saída. Passe o mouse ou abra a viagem.'}
        </span>
      </div>
    </section>
  );
}
